import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { api } from '@/utils/apiService';
import { isEmailVerificationRequired, isPublicTokenRoute, isPasswordChangeRequired } from '@/utils/authUtils';
import {
  shouldPreservePartnerSessionRoute,
  handlePartnerCheckoutResumeInAuthFlow,
} from '@/utils/partnerCheckoutReturn';
import { markFreshLogin } from '@/utils/communityBehavior';
import { notifyPartnerSessionReset } from '@/utils/partnerMembership.js';

const getLoginErrorCode = (response) => (
  response?.data?.error?.code || null
);

const actions = {
  // # API Init (Fetch-based) ##########
  initAxiosInstance: () => {
    // Obsolete with new fetch-based apiService, but kept for compatibility
    console.log('API: initAxiosInstance called (now using fetch)');
  },
  // ###################################

  // # Login ####################

  requestedLogin: async ({ commit }) => {
    try {
      const response = await api.post('/company/login');

      if (response.status === 401) {
        const errorCode = getLoginErrorCode(response);
        await commit('SET_LOGIN_CODE', null);
        return {
          ...response,
          failed: true,
          emailNotVerified: errorCode === 'email_not_verified',
        };
      }

      if (!response.data?.code) {
        await commit('SET_LOGIN_CODE', null);
        return { ...response, failed: true };
      }

      await commit('SET_LOGIN_CODE', response.data.code);
      return response;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  completeAuthFlow: async ({ dispatch }, { router, store }) => {
    const user = firebase.auth().currentUser;
    const currentRoute = router.currentRoute.value;
    const onPublicTokenRoute = isPublicTokenRoute(currentRoute);

    const { isKitchenKioskActive, resolveKitchenKioskTarget } = await import('@/utils/kitchenKiosk');
    const { isKitchenRoute } = await import('@/utils/authUtils');
    if (isKitchenKioskActive() && !isKitchenRoute(currentRoute)) {
      router.replace(resolveKitchenKioskTarget());
      return { kitchenTablet: true };
    }

    if (!user) {
      if (!onPublicTokenRoute) {
        router.push({ name: 'login' });
      }
      return;
    }

    if (isEmailVerificationRequired(user)) {
      if (
        currentRoute.name !== 'login.verify-email'
        && !onPublicTokenRoute
      ) {
        router.push({ name: 'login.verify-email' });
      }
      return;
    }

    let respons;
    try {
      respons = await dispatch('requestedLogin');
    } catch (e) {
      console.error('Login Error', e);
      return { failed: true };
    }

    if (respons?.emailNotVerified) {
      if (
        currentRoute.name !== 'login.verify-email'
        && !onPublicTokenRoute
      ) {
        router.push({ name: 'login.verify-email' });
      }
      return;
    }

    if (respons && respons.data?.code === 204) {
      if (!onPublicTokenRoute) {
        router.push({ name: 'login.profile' });
      }
      return;
    }

    if (respons && respons.data?.code === 409) {
      await dispatch('profile/requestCompany');

      const company = store?.getters?.['profile/getCompany'];
      if (isPasswordChangeRequired(company)) {
        if (!onPublicTokenRoute && currentRoute.name !== 'login.change-password') {
          router.push({ name: 'login.change-password' });
        }
        return { authenticated: true, passwordChangeRequired: true };
      }

      await dispatch('specials/requestSpecials');
      await dispatch('partner/load');
      await dispatch('tutorial/initTutorial');
      markFreshLogin();
      const preserveStripeReturn = shouldPreservePartnerSessionRoute(router, store);
      if (!onPublicTokenRoute && !preserveStripeReturn) {
        router.push({ name: 'navigation' });
      } else if (preserveStripeReturn) {
        await handlePartnerCheckoutResumeInAuthFlow(router);
      }
      if (store) {
        import('@/utils/liveSync').then(({ initLiveSync }) => {
          initLiveSync(store);
        });
      }
      return { authenticated: true };
    }

    if (respons && respons.data?.code === 423) {
      await firebase.auth().signOut();
      router.push({ name: 'login' });
      return { pendingAdminVerification: true };
    }

    if (respons?.failed || !respons) {
      await firebase.auth().signOut();
      router.push({ name: 'login' });
      return { failed: true };
    }
  },

  resetLoginCode: async ({ commit }) => {
    try {
      await commit('SET_LOGIN_CODE', null);
      return null;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  requestSetProfile: async ({ }, params) => {
    try {
      const response = await api.post('/company', params);
      return response;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  logout: async ({ commit, dispatch }) => {
    commit('SET_LOGOUT_IN_PROGRESS', true);
    try {
      await firebase.auth().signOut();
    } catch (e) {
      console.log('Logout error:', e);
    }
    try {
      await dispatch('clearSessionState');
    } catch (e) {
      // A failed cleanup must never keep the caller from leaving the app.
      console.log('Logout cleanup error:', e);
    } finally {
      commit('SET_LOGOUT_IN_PROGRESS', false);
    }
  },

  clearSessionState: async ({ commit, dispatch }) => {
    await dispatch('resetLoginCode');
    commit('profile/CLEAR_SESSION', null, { root: true });
    commit('specials/SET_SPECIALS', [], { root: true });
    commit('partner/CLEAR_SESSION', null, { root: true });
    commit('ownerPin/CLEAR', null, { root: true });
    notifyPartnerSessionReset();
  },
  // #################### Login #


  // # Cockpit ####################

  requestVisitors: async ({ }) => {
    try {
      const response = await api.get('/stats/couponCountPerMonth');
      return response;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  // #################### Cockpit #

  // # Profile ####################

  sendProfileImage: async ({ }, params) => {
    try {
      const response = await api.post('/image/company/', params);
      return response;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  // #################### Cockpit #


  // # Payment #####################
  injectPaymentInformation: async ({ dispatch }) => {
    try {
      await dispatch('requestPaymentInformation');
    } catch (err) {
      console.warn(err);
    }
  },

  requestPaymentInformation: async ({ commit }) => {
    const { data } = await api.get('/premium/packages');
    await commit('SET_PAYMENT_INFORMATION', data);
    const { boosts = [], subscriptions = [] } = data?.packages || {};
    const packages = [...boosts, ...subscriptions];
    await commit('SET_BOOSTS', boosts);
    await commit('SET_SUBSCRIPTIONS', subscriptions);
    await commit('SET_PACKAGES', packages);
    await commit('SET_STRIPE_SUBSCRIPTIONS', data.subscriptions || []);
    await commit('SET_STRIPE_SUBSCRIPTION_ITEMS', data.subscriptionItems || []);
    await commit('SET_STRIPE_PLANS', data.plans || []);
    await commit('SET_FREEMIUM', data.freemium || {});
  },

  requestStripeCustomerData: async ({ commit }) => {
    try {
      const response = await api.get('/stripe/payment/data/customer');
      if (response && response.data) {
        const { customer } = response.data;
        await commit('SET_STRIPE_CUSTOMER', customer);
      }
    } catch (err) {
      console.warn('Error: Failed to load Stripe Customer data');
    }
  },

  requestStripeSubscriptionsHistory: async ({ commit }) => {
    try {
      const response = await api.get('/stripe/payment/data/customer/subscriptions');
      if (response && response.data) {
        const { payload } = response.data;
        await commit('SET_STRIPE_SUBSCRIPTIONS_HISTORY', payload);
      }
    } catch (err) {
      console.warn('Error: Failed to load Stripe subscription history');
    }
  },
  requestStripeInvoicesHistory: async ({ commit }) => {
    try {
      const response = await api.get('/stripe/payment/data/customer/invoices');
      if (response && response.data) {
        const { payload } = response.data;
        await commit('SET_STRIPE_INVOICES_HISTORY', payload);
      }
    } catch (err) {
      console.warn('Error: Failed to load Stripe invoices history');
    }
  },

  subscribeCustomerToPlan: async ({ }, request) => {
    const response = { success: false, errorCode: null, subscription: null };
    const { planID, billingData, paymentMethod } = request;
    try {
      const requestBody = { planID, billingData, paymentMethod };
      const subscribeAnswer = await api.post('/stripe/payment/subscription', requestBody);
      if (subscribeAnswer && subscribeAnswer.data) {
        const { errorCode, subscription, additionalSteps } = subscribeAnswer.data;
        if (errorCode) {
          response.errorCode = errorCode;
          return response;
        }
        if (subscription) {
          response.success = true;
          response.subscription = subscription;
          response.additionalSteps = additionalSteps;
          return response;
        }
        response.errorCode = 'UNKNOWN_ERROR';
        return response;
      }


      return response;
    } catch (err) {
      response.errorCode = 'NETWORK_ERROR';
      return response;
    }
  },
  updateSubscription: async ({ }, request) => {
    const response = { success: false, errorCode: null, subscription: null };
    const { subscriptionID } = request;
    try {
      const updateAnswer = await api.patch(`/stripe/payment/subscription/${subscriptionID}/update`, request);
      if (updateAnswer && updateAnswer.data) {
        const { errorCode, subscription, additionalSteps } = updateAnswer.data;
        if (errorCode) {
          response.errorCode = errorCode;
          return response;
        }
        if (subscription) {
          response.success = true;
          response.subscription = subscription;
          response.additionalSteps = additionalSteps;
          return response;
        }
        response.errorCode = 'UNKNOWN_ERROR';
        return response;
      }
      return response;
    } catch (err) {
      response.errorCode = 'NETWORK_ERROR';
      return response;
    }
  },

  cancelSubscription: async ({ }, subscriptionID) => {
    const response = { success: false, errorCode: null, subscription: null };
    try {
      const requestBody = { subscription: subscriptionID };
      const subscribeAnswer = await api.post('/stripe/payment/subscription/cancel', requestBody);
      if (subscribeAnswer && subscribeAnswer.data) {
        const { errorCode, subscription } = subscribeAnswer.data;
        if (errorCode) {
          response.errorCode = errorCode;
          return response;
        }
        if (subscription) {
          response.success = true;
          response.subscription = subscription;
          return response;
        }
        response.errorCode = 'UNKNOWN_ERROR';
        return response;
      }


      return response;
    } catch (err) {
      console.log(err);
      response.errorCode = 'NETWORK_ERROR';
      return response;
    }
  },

  updateStripeCustomerData: async ({ }, updateData) => {
    const response = { success: false, errorCode: null, subscription: null };
    try {
      const requestBody = { updateData };
      const subscribeAnswer = await api.post('/stripe/payment/data/customer', requestBody);
      if (subscribeAnswer && subscribeAnswer.data) {
        const { errorCode, payload } = subscribeAnswer.data;
        if (errorCode) {
          response.errorCode = errorCode;
          return response;
        }
        if (payload) {
          response.success = true;
          response.subscription = payload;
          return response;
        }
        response.errorCode = 'UNKNOWN_ERROR';
        return response;
      }
      return response;
    } catch (err) {
      console.log(err);
      response.errorCode = 'NETWORK_ERROR';
      return response;
    }
  },

  renewPaymentData: async ({ dispatch }) => {
    await dispatch('requestPaymentInformation');
    await dispatch('requestStripeCustomerData');
  },

  requestPaymentIntent: async ({ }, params) => {
    try {
      const { planID } = params;
      const { data } = await api.get(`/stripe/payment/subscription/paymentintent/create/${planID}`);
      console.log(data);
      return data;
    } catch (err) {
      console.warn(`Request Payment Intent: ERR: ${err}`);
      return null;
    }
  },

  // ##### END Stripe

  // ##### START REPORT COUPON

  requestReportDownloadLink: async ({ }, params = {}) => {
    try {
      const { month, year, companyID } = params;
      const { data } = await api.get(`/company/${companyID}/report/generate/${year}/${month}`);
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  },

  downloadElement: async ({ }, params = {}) => {
    try {
      const { url } = params;
      const response = await api.get(url, { responseType: 'blob' });
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  },

  // ##### END REPORT COUPON

};

export default actions;
